<template>
  <v-container
    fluid
    class="dashboard-container pa-6">
    <!-- Header with Year Selection -->
    <v-row
      v-if="hasSquadMember"
      class="mb-6">
      <v-col cols="12">
        <div class="d-flex align-center justify-space-between">
          <h2 class="font-weight-bold">
            My Completion Rate
          </h2>
          <div class="year-selector">
            <v-btn-toggle
              v-model="selectedYear"
              mandatory
              rounded
              color="primary"
            >
              <v-btn
                v-for="year in yearOptions"
                :key="year.value"
                :value="year.value"
                class="px-6"
              >
                {{ year.value }}
              </v-btn>
            </v-btn-toggle>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-row
      v-if="hasSquadMember && dataReady && isDataEmpty()"
      class="empty-state-container-no-data"
    >
      <v-col
        cols="12">
        <div class="empty-state">
          <div class="empty-icon-wrapper">
            <v-icon
              size="100"
              color="grey lighten-1"
              class="mb-4 empty-icon"
            >
              mdi-folder-open-outline
            </v-icon>
          </div>
          <h2 class="text-h4 font-weight-bold grey--text text--darken-1 mb-3">
            ไม่พบข้อมูล
          </h2>
        </div>
      </v-col>
    </v-row>

    <!-- Completion Rate Cards -->
    <v-row v-if="hasSquadMember && !isDataEmpty()">
      <v-col cols="12">
        <h2 class="text-h5 font-weight-bold">
          Completion Rate
        </h2>
      </v-col>

      <!-- Loading Skeleton for Completion Rate -->
      <template v-if="loadingCompletionRate">
        <v-col
          v-for="i in 3"
          :key="'loading-completion-' + i"
          cols="12"
          sm="6"
          md="4"
        >
          <v-card
            class="completion-card pa-6 rounded-lg loading-pulse"
            elevation="2"
          >
            <div class="d-flex flex-column align-center">
              <v-skeleton-loader
                type="avatar"
                class="mb-3"
              ></v-skeleton-loader>
              <v-skeleton-loader
                type="text"
                width="100px"
                class="mb-2"
              ></v-skeleton-loader>
              <v-skeleton-loader
                type="heading"
                width="80px"
              ></v-skeleton-loader>
            </div>
          </v-card>
        </v-col>
      </template>

      <!-- Actual Completion Rate Data -->
      <template v-else>
        <v-col
          v-for="(completion) in completionRate"
          :key="completion.text"
          cols="12"
          sm="6"
          md="4"
        >
          <v-card
            class="completion-card pa-6 rounded-lg fade-in"
            :class="completion.cardClass"
            elevation="2"
            hover
          >
            <div class="d-flex flex-column align-center">
              <v-icon
                :color="completion.iconColor"
                size="48"
                class="mb-3 icon-bounce"
              >
                {{ completion.icon }}
              </v-icon>
              <span class="text-h6 font-weight-medium grey--text text--darken-2 mb-2">
                {{ completion.text }}
              </span>
              <span :class="'text-h2 font-weight-bold ' + completion.textColor">
                {{ completion.percent }}%
              </span>
            </div>
          </v-card>
        </v-col>
      </template>
    </v-row>

    <!-- Task Status Section -->
    <v-row v-if="hasSquadMember && !isDataEmpty()">
      <v-col cols="12">
        <h2 class="text-h5 font-weight-bold">
          Task Status
        </h2>
      </v-col>

      <!-- Loading Skeleton for Task Status -->
      <template v-if="loadingTaskStatus">
        <v-col
          v-for="i in 5"
          :key="'loading-task-' + i"
          cols="12"
          sm="6"
          md="4"
          lg="2.4"
        >
          <v-card
            class="task-card pa-5 rounded-lg loading-pulse"
            elevation="2"
          >
            <div class="d-flex flex-column">
              <div class="d-flex justify-space-between align-center mb-3">
                <v-skeleton-loader
                  type="chip"
                  width="80px"
                ></v-skeleton-loader>
                <v-skeleton-loader
                  type="avatar"
                  width="24px"
                  height="24px"
                ></v-skeleton-loader>
              </div>
              <v-skeleton-loader
                type="heading"
                width="60px"
                class="mb-2"
              ></v-skeleton-loader>
              <v-skeleton-loader
                type="text"
                width="40px"
              ></v-skeleton-loader>
            </div>
          </v-card>
        </v-col>
      </template>

      <!-- Actual Task Status Data -->
      <template v-else>
        <v-col
          v-for="task in tasks"
          :key="task.text"
          cols="12"
          sm="6"
          md="4"
          lg="2.4"
        >
          <v-card
            class="task-card pa-5 rounded-lg fade-in"
            :style="{'border-left': '4px solid ' + task.borderColor}"
            elevation="2"
            hover
          >
            <div class="d-flex flex-column">
              <div class="d-flex justify-space-between align-center mb-3">
                <v-chip
                  :color="task.chipColor"
                  text-color="white"
                  small
                >
                  {{ task.text }}
                </v-chip>
                <v-icon
                  :color="task.iconColor"
                  size="24"
                  class="icon-pulse"
                >
                  {{ task.icon }}
                </v-icon>
              </div>
              <span :class="'font-weight-bold ' + task.textSize2 + ' ' + task.textColor">
                {{ task.count }}
              </span>
              <span class="text-caption grey--text text--darken-1 mt-1">
                {{ task.count === 1 ? 'task' : 'tasks' }}
              </span>
            </div>
          </v-card>
        </v-col>
      </template>
    </v-row>

    <v-row
      v-if="!hasSquadMember && dataReady && !loading"
      class="empty-state-container"
    >
      <v-col
        cols="12">
        <div class="empty-state">
          <div class="empty-icon-wrapper">
            <v-icon
              size="100"
              color="grey lighten-1"
              class="mb-4 empty-icon"
            >
              mdi-folder-open-outline
            </v-icon>
          </div>
          <h2 class="text-h4 font-weight-bold grey--text text--darken-1 mb-3">
            ไม่พบข้อมูล
          </h2>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import PrototypeAttributeProvider from '@/resources/PrototypeAttributeProvider'
import ProductionPlanProvider from '@/resources/ProductionPlanProvider'
import { mapActions } from 'vuex/dist/vuex.common'

const PrototypeAttributeService = new PrototypeAttributeProvider()
const ProductionPlanService = new ProductionPlanProvider()

export default {
  data () {
    return {
      loadingCompletionRate: false,
      loadingTaskStatus: false,
      loading: false,
      selectedYear: this.$dayjs().year(),
      yearOptions: [
        { value: 2025 },
        { value: 2026 },
        { value: 2027 }
      ],
      completionRate: [
        {
          key: 'completedOnTime',
          text: 'On Time',
          percent: 0,
          textColor: 'success--text',
          iconColor: 'success',
          icon: 'mdi-check-circle',
          cardClass: 'success-card'
        },
        {
          key: 'completedOverdue',
          text: 'Overdue',
          percent: 0,
          textColor: 'orange--text',
          iconColor: 'orange',
          icon: 'mdi-clock-alert',
          cardClass: 'orange-card'
        },
        {
          key: 'completedCritical',
          text: 'Critical',
          percent: 0,
          textColor: 'red--text',
          iconColor: 'red',
          icon: 'mdi-alert-circle',
          cardClass: 'red-card'
        }
      ],
      tasks: [
        {
          key: 'critical',
          text: 'Critical',
          count: 0,
          textColor: 'error--text',
          textSize1: 'text-h6',
          textSize2: 'text-h3',
          chipColor: 'error',
          borderColor: '#f44336',
          iconColor: 'error',
          icon: 'mdi-alert-octagon'
        },
        {
          key: 'overdue',
          text: 'Overdue',
          count: 0,
          textColor: 'orange--text',
          textSize1: 'text-h6',
          textSize2: 'text-h3',
          chipColor: 'orange',
          borderColor: '#FF9800',
          iconColor: 'orange',
          icon: 'mdi-calendar-clock'
        },
        {
          key: 'dueSoon',
          text: 'Due Soon',
          count: 0,
          textColor: 'amber--text',
          textSize1: 'text-h6',
          textSize2: 'text-h3',
          chipColor: 'amber',
          borderColor: '#FFC107',
          iconColor: 'amber',
          icon: 'mdi-clock-fast'
        },
        {
          key: 'inProgress',
          text: 'In Progress',
          count: 0,
          textColor: 'indigo--text',
          textSize1: 'text-h6',
          textSize2: 'text-h3',
          chipColor: 'indigo',
          borderColor: '#3F51B5',
          iconColor: 'indigo',
          icon: 'mdi-progress-clock'
        },
        {
          key: 'pending',
          text: 'Pending',
          count: 0,
          textColor: 'grey--text text--darken-1',
          textSize1: 'text-h6',
          textSize2: 'text-h3',
          chipColor: 'grey',
          borderColor: '#9e9e9e',
          iconColor: 'grey',
          icon: 'mdi-pause-circle'
        }
      ],
      hasSquadMember: false,
      dataReady: false
    }
  },
  watch: {
    selectedYear () {
      this.getCompletionRate()
      this.getTaskStatus()
    }
  },
  async mounted () {
    await this.getSquadMember()
    if (this.hasSquadMember) {
      await Promise.all([
        this.getCompletionRate(),
        this.getTaskStatus()
      ])
    }
    this.dataReady = true
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar',
      setErrorPage: 'Components/setErrorPage',
      setModal: 'Components/setModal'
    }),
    async getSquadMember () {
      try {
        this.loading = true
        const { data } = await PrototypeAttributeService.getJobSquadMember()
        if (data) {
          this.hasSquadMember = true
        } else {
          this.hasSquadMember = false
        }
      } catch (error) {
        this.hasSquadMember = false
      } finally {
        this.loading = false
      }
    },
    isDataEmpty () {
      const isCompletionRateEmpty = this.completionRate.every((item) => item.percent === 0)
      const isTasksEmpty = this.tasks.every((item) => item.count === 0)
      return isCompletionRateEmpty && isTasksEmpty
    },
    async getCompletionRate () {
      try {
        if (!this.hasSquadMember) return

        this.loadingCompletionRate = true
        const { data } = await ProductionPlanService.getCompletionRate(this.selectedYear)
        if (data) {
          this.completionRate.forEach((item, index) => {
            this.completionRate[index].percent = data[item.key] || 0
          })
        }
      } catch (error) {
        console.error(error)
        this.setSnackbar({
          value: true,
          message: `Error: ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loadingCompletionRate = false
      }
    },
    async getTaskStatus () {
      if (!this.hasSquadMember) return
      try {
        this.loadingTaskStatus = true

        const { data } = await ProductionPlanService.getTaskStatus(this.selectedYear)
        if (data) {
          this.tasks.forEach((item, index) => {
            this.tasks[index].count = data[item.key] || 0
          })
        }
      } catch (error) {
        console.error(error)
        this.setSnackbar({
          value: true,
          message: `Error: ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loadingTaskStatus = false
      }
    }
  }
}
</script>

<style scoped>
.dashboard-container {
  background: #fff;
  min-height: calc(100vh - 70px);
  border-radius: 16px;
  position: relative;
}

.completion-card {
  transition: all 0.3s ease;
  background: white;
}

.completion-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15) !important;
}

.task-card {
  transition: all 0.3s ease;
  background: white;
}

.task-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15) !important;
}

.year-selector {
  background: white;
  border-radius: 50px;
  padding: 4px;
}

.success-card {
  border-top: 4px solid #4caf50;
}

.orange-card {
  border-top: 4px solid #FF9800;
}

.red-card {
  border-top: 4px solid #B71C1C;
}

.rounded-lg {
  border-radius: 16px !important;
}

.empty-state-container {
  min-height: calc(100vh - 100px);
  display: flex;
  align-items: center;
}

.empty-state-container-no-data {
  min-height: calc(100vh - 250px);
  display: flex;
  align-items: center;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>
